import { useState, type FormEvent } from "react";
import { ArrowLeft, KeyRound, ShieldCheck } from "lucide-react";
import { demoMode } from "../api";
import type { AdminIdentity } from "@player115/contracts";

export function LoginPage({ onLogin }: { onLogin: (username: string, password: string) => Promise<void> }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onLogin(username, password);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录失败");
    } finally {
      setBusy(false);
    }
  };
  return <main className="login-page">
    <section className="login-panel">
      <header><h1>管理员登录</h1><p>版本、权限与客户端状态</p></header>
      <form onSubmit={(event) => void submit(event)}>
        <label><span>登录账号</span><input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} /></label>
        <label><span>密码</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder={demoMode ? "演示模式输入任意非空密码" : "输入管理员密码"} /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" type="submit" disabled={busy || !username.trim() || !password}><KeyRound size={17} />{busy ? "正在验证" : "登录"}</button>
      </form>
    </section>
  </main>;
}

export function TwoFactorPage({ onVerify, onBack }: {
  onVerify: (code: string) => Promise<void>;
  onBack: () => void;
}) {
  const [code, setCode] = useState("");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onVerify(code);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "验证失败");
    } finally {
      setBusy(false);
    }
  };
  const switchMode = () => {
    setRecoveryMode((current) => !current);
    setCode("");
    setError("");
  };
  return <main className="login-page">
    <section className="login-panel">
      <header><h1>二次验证</h1><p>{recoveryMode ? "输入启用验证器时保存的一次性恢复码" : "打开 Google Authenticator，输入 6 位动态验证码"}</p></header>
      <form onSubmit={(event) => void submit(event)}>
        <label><span>{recoveryMode ? "恢复码" : "验证码"}</span><input
          className={recoveryMode ? "recovery-input" : "code-input"}
          autoFocus
          autoComplete="one-time-code"
          inputMode={recoveryMode ? "text" : "numeric"}
          maxLength={recoveryMode ? 19 : 6}
          value={code}
          onChange={(event) => setCode(recoveryMode
            ? event.target.value.toUpperCase().replace(/[^A-F0-9-]/g, "")
            : event.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder={recoveryMode ? "XXXX-XXXX-XXXX-XXXX" : "6 位数字"}
        /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" type="submit" disabled={busy || (recoveryMode ? code.replace(/-/g, "").length !== 16 : code.length !== 6)}><KeyRound size={17} />{busy ? "正在验证" : "验证并登录"}</button>
      </form>
      <div className="login-options">
        <button type="button" onClick={switchMode}>{recoveryMode ? "使用动态验证码" : "手机无法使用？使用恢复码"}</button>
        <button type="button" onClick={onBack}><ArrowLeft size={14} />返回</button>
      </div>
    </section>
  </main>;
}

export function PasswordChangePage({ admin, onChange, onLogout }: {
  admin: AdminIdentity;
  onChange: (password: string) => Promise<void>;
  onLogout: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirmation) {
      setError("两次输入的密码不一致");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onChange(password);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "密码修改失败");
    } finally {
      setBusy(false);
    }
  };
  return <main className="login-page">
    <section className="login-panel">
      <div className="login-brand"><span><ShieldCheck size={24} /></span><div><strong>115影音控制台</strong><small>{admin.email}</small></div></div>
      <header><h1>设置管理员密码</h1><p>首次登录必须更换初始密码，完成后才能进入控制台。</p></header>
      <form onSubmit={(event) => void submit(event)}>
        <label><span>新密码</span><input type="password" autoComplete="new-password" minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="至少 12 个字符" /></label>
        <label><span>确认新密码</span><input type="password" autoComplete="new-password" minLength={12} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" type="submit" disabled={busy || password.length < 12 || password !== confirmation}><KeyRound size={17} />{busy ? "正在保存" : "保存新密码"}</button>
      </form>
      <footer><span>当前账号</span><button className="text-button" type="button" onClick={onLogout}>{admin.username} · 退出登录</button></footer>
    </section>
  </main>;
}
